import { EProjectAuthType, EProjectPublishMethod, ICommonSettings, IConfig } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import _ from "lodash";
import ObjectHash from "object-hash";
import { ValidateErrorEntity } from "rc-field-form/es/interface";
import { KeyboardEvent, PureComponent, ReactNode, createRef } from "react";
import { connect } from "react-redux";
import { ConfigService } from "../../../services/config-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import {
  setActiveGlobalConfiguration,
  setGlobalConfigs,
  setGlobalConfigsLoadingStatus
} from "../../../store/global-config/actions";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { AndroidConfig } from "../../design-tools/app-settings/app-settings-config-manager/app-settings-config-android";
import "../../design-tools/app-settings/config-manager.scss";
import {
  ICommonConfigDeleteFunctionParams,
  ICommonConfigSaveFunctionParams
} from "../../design-tools/app-settings/app-settings-config-manager/app-settings-config-container";
import { IOSConfig } from "../../design-tools/app-settings/app-settings-config-manager/app-settings-config-ios";
import { AppSettings } from "../../design-tools/app-settings/app-settings-config-manager/app-settings-config-main";
import { WebConfig } from "../../design-tools/app-settings/app-settings-config-manager/app-settings-config-web";
import { ConfigPaneKey } from "../../design-tools/app-settings/app-settings-config-manager/config-categories";
import {
  changedPathsBetween,
  IConfigFormValues,
  snapshotValueAt
} from "../../design-tools/app-settings/app-settings-config-manager/config-change-tracking";
import { ConfigSearch } from "../../design-tools/app-settings/app-settings-config-manager/config-search";
import {
  ConfigPaneContext,
  ConfigRevealContext,
  ConfigSearchIndexContext,
  ConfigSearchSlotContext,
  IConfigRevealTarget
} from "../../design-tools/app-settings/app-settings-config-manager/config-search-context";
import {
  ConfigSearchIndex,
  IConfigSearchPane
} from "../../design-tools/app-settings/app-settings-config-manager/config-search-index";
import { ConfigEditorHeader } from "../../design-tools/app-settings/config-editor-header";
import { normalizeConfigName, validateConfigName } from "../../design-tools/app-settings/config-manager-model";
import { requestConfigReveal } from "../../design-tools/app-settings/config-reveal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaListPage from "../../ui-kit/kuika-list-page/kuika-list-page";
import { KuikaListTableRowAction } from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaTabContent from "../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";

/**
 * The editor's antd form. The header's SAVE button sits OUTSIDE it — `KuikaListPageHeader`
 * draws the actions row — so it submits by `form=` rather than by nesting.
 */
const GLOBAL_CONFIG_FORM_ID: string = "globalConfigForm";

interface IGlobalConfigTabOption {
  value: ConfigPaneKey;
  label: string;
  dataTestId: string;
}

/**
 * Four, not five: a workspace configuration has no Desktop App pane. `IConfig` carries
 * `electronConfig`, but nothing here has ever rendered or written it.
 */
const GLOBAL_CONFIG_TABS: IGlobalConfigTabOption[] = [
  { value: "apiConfig", label: "App Settings", dataTestId: "globalAppSettingsTab" },
  { value: "webConfig", label: "Web", dataTestId: "globalWebConfigTab" },
  { value: "iosConfig", label: "IOS", dataTestId: "globalIosConfigTab" },
  { value: "androidConfig", label: "Android", dataTestId: "globalAndroidConfigTab" }
];

/** The panes in the strip's order, so the incoming pane slides in from the side the tab moved to. */
const GLOBAL_CONFIG_TAB_ORDER: string[] = GLOBAL_CONFIG_TABS.map((tab: IGlobalConfigTabOption): string => tab.value);

export interface IGlobalConfigsContainerProps {
  /** The configuration list's own row actions, for the editor switcher's "…". */
  rowActions?: KuikaListTableRowAction<IConfig>[];
  globalConfigs: IConfig[];
  globalCommonSettings: ICommonSettings;
  activeGlobalConfiguration?: IConfig;
  isCreateGlobalConfigActive: boolean;
  showModal: boolean;
  setActiveGlobalConfiguration: (config?: IConfig) => void;
  setGlobalConfigs: (globalConfigs: IConfig[], globalCommonSettings: ICommonSettings) => void;
  setGlobalConfigsLoadingStatus: (isGlobalConfigsLoading: boolean) => void;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}
export interface IGlobalConfigsContainerState {
  activeConfigTab: ConfigPaneKey;
  /** The setting a search result is pointing at, or null when nothing was asked for. */
  revealTarget: IConfigRevealTarget | null;
  /** The settings search's query — see the twin in `app-settings-config-container`. */
  searchQuery: string;
  configName: string;
  isConfigNameOnEdit: boolean;
  /** Form paths edited since the configuration was opened. Drives the rail's dots and the orange labels. */
  changedPaths: ReadonlySet<string>;
  /** Where SAVE's commit stands — see `SaveStatus`, which owns the ✓-then-close order. */
  saveStatus: KuikaButtonStatus;
}

class GlobalConfigsContainer extends PureComponent<
  IGlobalConfigsContainerProps & IWithSDProps,
  IGlobalConfigsContainerState
> {
  objectHash: string = "";

  /** The form as it stood when the configuration was opened, or when it was last saved. */
  savedSnapshot: IConfigFormValues = {};

  formRef = createRef<FormInstance>();

  /** What the settings search reads; the sections and fields fill it as they mount. */
  searchIndex: ConfigSearchIndex = new ConfigSearchIndex();

  /** Bumped per selection, so choosing the same result twice marks the row twice. */
  revealToken: number = 0;

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props) {
    super(props);
    this.state = {
      activeConfigTab: "apiConfig",
      revealTarget: null,
      searchQuery: "",
      configName: props.activeGlobalConfiguration?.name || "",
      // Seeded from the open configuration rather than hard-coded true: `loadConfig` fixes
      // it a tick later either way, and starting true showed the rename input for a frame
      // on every open.
      isConfigNameOnEdit: !props.activeGlobalConfiguration?.name,
      changedPaths: new Set<string>(),
      saveStatus: "idle"
    };
  }

  componentDidMount(): void {
    this.loadConfig();
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(
    prevProps: Readonly<IGlobalConfigsContainerProps>,
    _prevState: Readonly<IGlobalConfigsContainerState>,
    _snapshot?: any
  ): void {
    const { nextCallback, activeGlobalConfiguration, isCreateGlobalConfigActive } = this.props;

    if (prevProps.activeGlobalConfiguration?.id !== activeGlobalConfiguration?.id) {
      this.formRef.current?.resetFields();
      this.setState(
        {
          activeConfigTab: "apiConfig",
          // Creating used to land in the rename input, because the hero that named the
          // configuration was not the field the editor read. The name modal names it before
          // the editor opens, so the input is only needed when the name is really missing.
          isConfigNameOnEdit: isCreateGlobalConfigActive && !activeGlobalConfiguration?.name,
          configName: activeGlobalConfiguration?.name || ""
        },
        () => {
          this.loadConfig();
        }
      );
    }

    if (nextCallback) {
      // A save the pending navigation asked for ("SAVE & CLOSE") is still running or showing its
      // ✓, and its own close runs the navigation. Asking again now would re-open the confirmation.
      if (this.saveStatus.canStart) {
        this.handleCloseConfig();
      }
      return;
    }
  }

  loadConfig() {
    const { activeGlobalConfiguration, isCreateGlobalConfigActive } = this.props;

    this.setState(
      {
        configName: activeGlobalConfiguration?.name || "",
        isConfigNameOnEdit: isCreateGlobalConfigActive && !activeGlobalConfiguration?.name
      },
      () => {
        const formValues: any = {
          ...activeGlobalConfiguration
        };

        if (activeGlobalConfiguration?.apiConfig) {
          let mailSettingsId: string | null = null;
          let workflowMailSettingsId: string | null = null;

          if (activeGlobalConfiguration?.apiConfig.smtpSettingsId) {
            mailSettingsId = `smtp|${activeGlobalConfiguration.apiConfig?.smtpSettingsId}`;
          } else if (activeGlobalConfiguration?.apiConfig.awsSettingsId) {
            mailSettingsId = `aws|${activeGlobalConfiguration.apiConfig?.awsSettingsId}`;
          } else if (activeGlobalConfiguration?.apiConfig.exchangeServerMailSettingsId) {
            mailSettingsId = `exchangeServerMail|${activeGlobalConfiguration.apiConfig?.exchangeServerMailSettingsId}`;
          }
          if (activeGlobalConfiguration?.apiConfig.workflowSmtpSettingsId) {
            workflowMailSettingsId = `smtp|${activeGlobalConfiguration.apiConfig?.workflowSmtpSettingsId}`;
          } else if (activeGlobalConfiguration?.apiConfig.workflowAwsSettingsId) {
            workflowMailSettingsId = `aws|${activeGlobalConfiguration.apiConfig?.workflowAwsSettingsId}`;
          }

          formValues.apiConfig.mailSettingsId = mailSettingsId;
          formValues.apiConfig.workflowMailSettingsId = workflowMailSettingsId;
        }

        this.formRef.current?.setFieldsValue(formValues);
        this.objectHash = this.getObjectHash();
        this.takeChangeSnapshot();
      }
    );
  }

  handleFormError = ({ errorFields }: ValidateErrorEntity<any>) => {
    for (let i = 0; i < errorFields.length; i++) {
      if (errorFields[i].name.length < 2) continue;
      // A form path is `[slice, key]` and a slice IS a pane, which is what lets a failed
      // validation open the pane holding the field that failed.
      const parentData = errorFields[i].name[0] as ConfigPaneKey;

      if (parentData === this.state.activeConfigTab) return;

      this.setState({ activeConfigTab: parentData });
      return;
    }
  };

  handleCloseConfig = () => {
    if (this.objectHash === this.getObjectHash()) {
      this.closeConfig();
      return;
    }

    this.props.setShowModal(true);
  };

  closeConfig = () => {
    if (runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      return;
    }
    this.props.setActiveGlobalConfiguration();
  };

  handleTabChange = (activeConfigTab: string) => {
    this.setState({ activeConfigTab: activeConfigTab as ConfigPaneKey });
  };

  /**
   * Take a search result to the setting it names. Same three steps as the project editor:
   * the platform tab here, the rail inside `ConfigPaneShell`, then the row itself. A
   * workspace configuration supports every platform, so nothing is hidden and no pane has
   * to be excluded — the Desktop App pane simply does not exist here.
   */
  handleReveal = (target: Omit<IConfigRevealTarget, "token">): void => {
    if (!GLOBAL_CONFIG_TABS.some((tab: IGlobalConfigTabOption): boolean => tab.value === target.pane)) return;
    this.revealToken += 1;
    this.setState({ activeConfigTab: target.pane, revealTarget: { ...target, token: this.revealToken } });
  };

  getSearchPanes = (): IConfigSearchPane[] =>
    GLOBAL_CONFIG_TABS.map((tab: IGlobalConfigTabOption): IConfigSearchPane => ({
      key: tab.value,
      label: tab.label
    }));

  getSectionDescription = (contentKey: string): ReactNode => {
    const { configLocalizations } = this.props.sd;
    const content = configLocalizations.find((localization) => localization.key === contentKey);
    if (!content) return undefined;

    const isMainAi = KMainFunctions.isMainAI();

    return (
      <>
        {content.description}
        {!!content.helpLink && !isMainAi && (
          <a href={content.helpLink} target="_blank" rel="noreferrer">
            {" "}
            Learn more
          </a>
        )}
      </>
    );
  };

  getObjectHash = () => {
    const { configName } = this.state;
    // `getFieldsValue(true)`, not the mounted-only `getFieldsValue()`: Swagger UI Credentials'
    // mode sits behind its own `useSwaggerUi` gate (see the twin comment in
    // `app-settings-config-container.tsx`), so the "unsaved changes?" prompt on close must
    // not silently miss an edit to it just because the field is unmounted at the moment of
    // asking.
    const data = this.formRef.current?.getFieldsValue(true);

    const hashObj = {
      ...data,
      configName
    };

    return ObjectHash(hashObj);
  };

  handleFetchConfigs = (callback: () => void) => {
    ConfigService.getGlobalConfigs()
      .then(({ data: { configurations, commonSettings } }) => {
        this.props.setGlobalConfigs(configurations, commonSettings);
        callback();
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
      })
      .finally(() => {
        this.props.setGlobalConfigsLoadingStatus(false);
      });
  };

  createCommonConfig = ({
    formInputName,
    customizedId,
    configKey,
    config,
    onSuccess,
    onError = () => {},
    onFinal
  }: ICommonConfigSaveFunctionParams) => {
    this.props.setGlobalConfigsLoadingStatus(true);
    ConfigService.createGlobalCommonConfig(configKey, config)
      .then(() => {
        onSuccess();
        this.handleFetchConfigs(() => {
          this.handleUpdateCustomFormInput(formInputName, customizedId || config.id);
        });
        this.props.sd.fetchWhatsappBusinessTemplates();
      })
      .catch((error) => {
        this.props.setGlobalConfigsLoadingStatus(false);
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  updateCommonConfig = ({
    formInputName,
    customizedId,
    configKey,
    config,
    onSuccess,
    onError = () => {},
    onFinal
  }: ICommonConfigSaveFunctionParams) => {
    this.props.setGlobalConfigsLoadingStatus(true);
    ConfigService.updateGlobalCommonConfig(configKey, config)
      .then(() => {
        onSuccess();
        this.handleFetchConfigs(() => {
          this.handleUpdateCustomFormInput(formInputName, customizedId || config.id);
        });
        this.props.sd.fetchWhatsappBusinessTemplates();
      })
      .catch((error) => {
        this.props.setGlobalConfigsLoadingStatus(false);
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  deleteCommonConfig = ({
    formInputName,
    configKey,
    configId,
    onSuccess,
    onError = () => {},
    shouldControlOtherSections,
    onFinal
  }: ICommonConfigDeleteFunctionParams) => {
    this.props.setGlobalConfigsLoadingStatus(true);
    if (shouldControlOtherSections) {
      if (this.isCommonConfigUsedOnOtherSections(formInputName, shouldControlOtherSections.listOfControl)) {
        this.props.setGlobalConfigsLoadingStatus(false);
        shouldControlOtherSections.onCannotDelete();
        onFinal();
        return;
      }
    }
    ConfigService.deleteGlobalCommonConfig(this.props.activeGlobalConfiguration?.id || "", configKey, configId)
      .then(({ data: { isSuccess, errorMessage } }) => {
        if (!isSuccess) {
          KMainFunctions.displayWarningNotification(errorMessage ?? "Something went wrong while deleting the setting");
          onError();
          return;
        }
        onSuccess();
        this.handleFetchConfigs(() => {
          return this.handleUpdateCustomFormInput(formInputName, null);
        });
        this.props.sd.fetchWhatsappBusinessTemplates();
      })
      .catch((error) => {
        this.props.setGlobalConfigsLoadingStatus(false);
        KMainFunctions.exceptionHandler(error);
        onError();
      })
      .finally(() => {
        onFinal();
      });
  };

  isCommonConfigUsedOnOtherSections(formInputName: string[], listOfControl: string[][]): boolean {
    const formValues: IConfig | undefined = this.formRef.current?.getFieldsValue();
    if (!formValues) return false;

    for (let i = 0; i < listOfControl.length; i++) {
      const parentDataKey = listOfControl[i][0];
      const childDataKey = listOfControl[i][1];

      if (!formValues[parentDataKey]) continue;
      if (parentDataKey === formInputName[0] && childDataKey === formInputName[1]) continue;

      if (formValues[parentDataKey][childDataKey] === formValues[formInputName[0]][formInputName[1]]) return true;
    }

    return false;
  }

  handleUpdateCustomFormInput = (formItemName: string[], value: string | null) => {
    const fieldsValue = { ...this.formRef.current?.getFieldsValue() };
    fieldsValue[formItemName[0]][formItemName[1]] = value;
    this.formRef.current?.setFieldsValue(fieldsValue);
    this.recomputeChangedPaths();
  };

  /**
   * Reset what "changed" is measured against — on load, and again after a save lands.
   *
   * `getFieldsValue(true)`, not the mounted-only `getFieldsValue()` — see the twin comment in
   * `app-settings-config-container.tsx`'s `takeChangeSnapshot`: Swagger UI Credentials' mode
   * can be unmounted at the exact moment the snapshot is taken, and `true` is what keeps it in
   * the snapshot regardless.
   */
  takeChangeSnapshot = (): void => {
    this.savedSnapshot = _.cloneDeep(this.formRef.current?.getFieldsValue(true) ?? {}) as IConfigFormValues;
    if (this.state.changedPaths.size > 0) this.setState({ changedPaths: new Set<string>() });
  };

  /**
   * Put the named fields back to what they were when the configuration was opened.
   *
   * The orange marks are the control: the label undoes its own field, the rail's dot undoes
   * everything in its category. It goes through the same writer a row's Restore uses, so
   * the difference against the parent is recomputed on the way as it always is.
   */
  handleRevertPaths = (paths: string[]): void => {
    paths.forEach((path: string): void => {
      const [slice, key] = path.split(".");
      // A root-level path — `swaggerUiMode` — has no key segment; write it directly rather
      // than through `handleUpdateCustomFormInput`, which indexes two levels deep.
      if (key === undefined) {
        this.formRef.current?.setFieldsValue({ [slice]: snapshotValueAt(this.savedSnapshot, path) });
        this.recomputeChangedPaths();
        return;
      }
      this.handleUpdateCustomFormInput([slice, key], snapshotValueAt(this.savedSnapshot, path) as string | null);
    });
  };

  /**
   * Re-read which fields differ from the snapshot. Driven by the form's own change event and
   * by `handleUpdateCustomFormInput`, which writes the store without firing one.
   *
   * `getFieldsValue(true)` — the snapshot and the current read have to share the same
   * mounting semantics, or a value present in one but absent from the other (mounted-only)
   * reads as a change nobody made.
   */
  recomputeChangedPaths = (): void => {
    const current: IConfigFormValues = (this.formRef.current?.getFieldsValue(true) ?? {}) as IConfigFormValues;
    const next: Set<string> = changedPathsBetween(current, this.savedSnapshot);
    const previous: ReadonlySet<string> = this.state.changedPaths;
    if (next.size === previous.size && [...next].every((path: string): boolean => previous.has(path))) return;
    this.setState({ changedPaths: next });
  };

  handleSubmit = async (formValues) => {
    if (!this.props.activeGlobalConfiguration) return;
    // SAVE is live again the moment its ✓ replaces the spinner, so a second Enter would re-fire
    // the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    const {
      activeGlobalConfiguration: { id, configurationVersion, isSelected, isRunConfig, name: originalName },
      globalCommonSettings: { genericAuthSettings, oAuth2Settings, ldapSettings }
    } = this.props;

    // Defense in depth — see the twin comment in `app-settings-config-container`: the field
    // is unmounted whenever the rename is not open, and SAVE otherwise reads whatever
    // `configName` last held without re-checking it against the live workspace list.
    const nameToSave: string = normalizeConfigName(formValues.name ?? this.state.configName);
    const nameError: string | undefined = validateConfigName(nameToSave, {
      configs: this.props.globalConfigs,
      selfId: id,
      originalName
    });
    if (nameError) {
      this.formRef.current?.setFieldsValue({ name: nameToSave });
      this.setState({ isConfigNameOnEdit: true }, () => {
        this.formRef.current?.validateFields(["name"]).catch(() => {});
      });
      return;
    }

    const publishMethods: string = `${EProjectPublishMethod.webUi},${EProjectPublishMethod.android},${EProjectPublishMethod.ios}`;

    const config: IConfig = {
      // Form Values
      ...formValues,
      // The name field is mounted only while the rename is open, and a field that is not
      // mounted is absent from `onFinish`'s values — so a configuration saved without
      // touching its name was being saved with no name at all. `nameToSave` is the
      // committed, normalized name in either mode, already validated above.
      name: nameToSave,
      supportAndroid: true,
      supportIOS: true,
      supportWeb: true,
      publishMethods,
      // Form Excluded Values
      id,
      configurationVersion,
      isSelected,
      isRunConfig
    };
    if (formValues.apiConfig.mailSettingsId) {
      const mailSettings = formValues.apiConfig.mailSettingsId.split("|");
      if (mailSettings[0] === "smtp") {
        config.apiConfig.smtpSettingsId = mailSettings[1];
      } else if (mailSettings[0] === "aws") {
        config.apiConfig.awsSettingsId = mailSettings[1];
      } else if (mailSettings[0] === "exchangeServerMail") {
        config.apiConfig.exchangeServerMailSettingsId = mailSettings[1];
      }
    }

    if (formValues.apiConfig.workflowMailSettingsId) {
      const workflowMailSettings = formValues.apiConfig.workflowMailSettingsId.split("|");
      if (workflowMailSettings[0] === "smtp") {
        config.apiConfig.workflowSmtpSettingsId = workflowMailSettings[1];
      } else {
        config.apiConfig.workflowAwsSettingsId = workflowMailSettings[1];
      }
    }

    formValues.apiConfig.authProviderType = EProjectAuthType.none;
    if (formValues.apiConfig.authProviderSettingsId) {
      if (genericAuthSettings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.genericAuth;
      } else if (oAuth2Settings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.oAuth2;
      } else if (ldapSettings?.find((x) => x.id === formValues.apiConfig.authProviderSettingsId)) {
        formValues.apiConfig.authProviderType = EProjectAuthType.ldap;
      }
    }

    if (this.props.isCreateGlobalConfigActive) {
      this.createConfig(config);
      return;
    }
    this.updateConfig(config);
  };

  createConfig(formValues: IConfig) {
    this.saveStatus.start();
    this.props.setGlobalConfigsLoadingStatus(true);
    ConfigService.createGlobalConfig(formValues)
      .then((_response) => {
        this.handleSaved(formValues.name);
      })
      .catch((error) => {
        this.props.setGlobalConfigsLoadingStatus(false);
        this.saveStatus.fail();
        KMainFunctions.exceptionHandler(error);
      });
  }

  updateConfig(formValues: IConfig) {
    this.saveStatus.start();
    this.props.setGlobalConfigsLoadingStatus(true);
    ConfigService.updateGlobalConfig(formValues)
      .then((_response) => {
        this.handleSaved(formValues.name);
      })
      .catch((error) => {
        this.props.setGlobalConfigsLoadingStatus(false);
        this.saveStatus.fail();
        KMainFunctions.exceptionHandler(error);
      });
  }

  /**
   * A create or an update landed. SAVE shows its ✓ first and the editor leaves after it, onto
   * the landing, which lights the configuration's row once the refreshed list carries it. That
   * pair is the confirmation, so there is no "has been created / updated" toast on top of it.
   */
  handleSaved = (name: string): void => {
    // What is on screen is what was saved: a Back pressed while the ✓ is up has nothing to ask about.
    this.objectHash = this.getObjectHash();
    // A close confirmation this save answered ("SAVE & CLOSE") steps aside at once, so the ✓ on
    // SAVE is what is seen before the editor leaves.
    this.props.setShowModal(false);
    // The page lock held the editor still while the write ran; it comes off before the ✓, which it
    // would otherwise dim. The list refetches while the ✓ is up.
    this.props.setGlobalConfigsLoadingStatus(false);
    this.handleFetchConfigs(() => {});
    this.props.sd.fetchWhatsappBusinessTemplates();
    requestConfigReveal({ scope: "global", name });
    this.saveStatus.succeed(this.closeConfig);
  };

  /** The settings search, drawn above the rail of the pane on screen. One box, not four. */
  renderSearch = (): ReactNode => (
    <ConfigSearch
      index={this.searchIndex}
      panes={this.getSearchPanes()}
      // A workspace configuration supports every platform, so no pane is hidden.
      hiddenPanes={[]}
      // Empty on this surface today: the workspace editor fetches no localizations (see the
      // note in `config-modal-parts.tsx`), so a field whose only prose is a tooltip is
      // searchable by its label alone. Wired anyway — the fix is to fetch them.
      localizations={this.props.sd.configLocalizations}
      query={this.state.searchQuery}
      onQueryChange={(searchQuery: string): void => this.setState({ searchQuery })}
      onReveal={this.handleReveal}
    />
  );

  /**
   * One platform's pane, keyed by its tab so `KuikaTabContent` can tell the panes apart. It is
   * mounted always and shown only when active — see the note at the panes container: one form
   * spans every pane, so hiding never unmounts.
   */
  renderPane = (key: ConfigPaneKey, body: ReactNode): ReactNode => (
    <div key={key} className="kuika-config-editor__pane" data-config-pane={key}>
      {/* Which pane a subtree is in is the one thing a section cannot work out for itself,
          and it is half of what a search result needs to get back to it. */}
      <ConfigPaneContext.Provider value={key}>
        <ConfigSearchSlotContext.Provider value={this.state.activeConfigTab === key ? this.renderSearch() : null}>
          {body}
        </ConfigSearchSlotContext.Provider>
      </ConfigPaneContext.Provider>
    </div>
  );

  /**
   * Switching runs through the unsaved-changes callback, exactly as the deleted left panel's
   * row click did — the dirty check has to happen before the open configuration is replaced.
   */
  handleSwitchConfig = (config: IConfig): void => {
    const change = (): void => {
      this.props.setActiveGlobalConfiguration(config);
    };

    if (this.props.activeGlobalConfiguration) {
      this.props.setNextCallbackFunction(change);
      return;
    }

    change();
  };

  /**
   * Resolves true when the name field's own rules pass. Awaited rather than read off
   * `getFieldError`: the field is mounted fresh each time the pencil is clicked and
   * `configNameRules`'s uniqueness check is async, so the first Enter would see no error
   * recorded yet.
   */
  validateName = async (): Promise<boolean> => {
    try {
      await this.formRef.current?.validateFields(["name"]);
      return true;
    } catch {
      return false;
    }
  };

  handleSubmitNameChange = () => {
    // `validateName` has already run the field's rules; this commits the result, normalized.
    const normalized: string = normalizeConfigName(this.formRef.current?.getFieldValue("name") ?? "");
    this.formRef.current?.setFieldsValue({ name: normalized });
    this.setState({ isConfigNameOnEdit: false, configName: normalized });
  };

  handleAbortNameChange = () => {
    if (!this.state.configName) {
      KMainFunctions.displayWarningNotification("Please enter a valid configuration name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.configName
    });
    this.setState({ isConfigNameOnEdit: false });
  };

  handleNameInputOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    this.handleSubmitNameChange();
  };

  render() {
    return (
      <>
        <Form
          id={GLOBAL_CONFIG_FORM_ID}
          className="kuika-config-editor__form"
          ref={this.formRef}
          onFinishFailed={this.handleFormError}
          onFinish={this.handleSubmit}
          onFieldsChange={this.recomputeChangedPaths}
        >
          <ConfigEditorHeader
            tabs={
              <KuikaTabs
                size="lg"
                options={GLOBAL_CONFIG_TABS}
                value={this.state.activeConfigTab}
                onChange={this.handleTabChange}
                dataTestId="globalConfigTabs"
              />
            }
            configName={this.state.configName}
            isNameOnEdit={this.state.isConfigNameOnEdit}
            onStartRename={() => this.setState({ isConfigNameOnEdit: true })}
            onSubmitName={this.handleSubmitNameChange}
            onAbortRename={this.handleAbortNameChange}
            nameScope={this.props.globalConfigs}
            selfId={this.props.activeGlobalConfiguration?.id}
            originalName={this.props.activeGlobalConfiguration?.name}
            validateName={this.validateName}
            configs={this.props.globalConfigs}
            currentConfig={this.props.activeGlobalConfiguration}
            onSwitchConfig={this.handleSwitchConfig}
            onBack={this.handleCloseConfig}
            rowActions={this.props.rowActions}
            saveLabel={this.props.isCreateGlobalConfigActive ? "SAVE" : "UPDATE"}
            status={this.state.saveStatus}
            formId={GLOBAL_CONFIG_FORM_ID}
            editIconTestId="editGlobalConfigNameIcon"
            submitIconTestId="submitGlobalConfigNameIcon"
            nameInputTestId="globalConfigNameUpdateInput"
            nameTestId="globalConfigHeaderName"
            cancelTestId="globalConfigCancelButton"
            saveTestId="globalConfigUpdateSaveButton"
          />
          {/* The bar is the form's first row and this the scrolling rest of it. */}
          <KuikaListPage className="kuika-config-editor" data-testid="globalConfigEditorScreen" belowBar>
            {/* Every pane stays mounted (`keepMounted`) and the inactive ones are hidden. One
              antd Form spans all of them — `handleSubmit` rebuilds the configuration from
              `getFieldsValue()` — so unmounting a pane would silently drop its values on
              save. This is what antd's `forceRender` was doing. */}
            {/* The index the settings register into, and the target a search result sets, both
              span every pane — the search box is in the header and the settings are down in
              the leaves. */}
            <ConfigSearchIndexContext.Provider value={this.searchIndex}>
              <ConfigRevealContext.Provider value={this.state.revealTarget}>
                <div className="kuika-config-editor__panes">
                  <div className="kuika_app-settings__content__container__config-manager">
                    {/* Every pane is a direct keyed child: the frame finds its panes by key. */}
                    <KuikaTabContent
                      className="kuika-config-editor__tab-content"
                      activeKey={this.state.activeConfigTab}
                      order={GLOBAL_CONFIG_TAB_ORDER}
                      keepMounted
                    >
                      {this.renderPane(
                        "apiConfig",
                        <AppSettings
                          isGlobalConfig
                          changedPaths={this.state.changedPaths}
                          onRevertPaths={this.handleRevertPaths}
                          commonSettings={this.props.globalCommonSettings}
                          datasources={[]}
                          getSectionDescription={this.getSectionDescription}
                          handleUpdateCustomFormInput={() => {}}
                          handleCreateCommonConfig={this.createCommonConfig}
                          handleUpdateCommonConfig={this.updateCommonConfig}
                          handleDeleteCommonConfig={this.deleteCommonConfig}
                        />
                      )}
                      {this.renderPane(
                        "webConfig",
                        <WebConfig
                          isGlobalConfig
                          isSupporting={true}
                          changedPaths={this.state.changedPaths}
                          onRevertPaths={this.handleRevertPaths}
                          toggleSupport={() => {}}
                          handleGetImageLibrary={() => {}}
                          commonSettings={this.props.globalCommonSettings}
                          languages={this.props.sd.project.languages}
                          getSectionDescription={this.getSectionDescription}
                          handleUpdateCustomFormInput={() => {}}
                          handleCreateCommonConfig={this.createCommonConfig}
                          handleUpdateCommonConfig={this.updateCommonConfig}
                          handleDeleteCommonConfig={this.deleteCommonConfig}
                        />
                      )}
                      {this.renderPane(
                        "iosConfig",
                        <IOSConfig
                          isGlobalConfig
                          isSupporting={true}
                          changedPaths={this.state.changedPaths}
                          onRevertPaths={this.handleRevertPaths}
                          toggleSupport={() => {}}
                          handleGetImageLibrary={() => {}}
                          languages={[]}
                          commonSettings={this.props.globalCommonSettings}
                          getSectionDescription={this.getSectionDescription}
                          handleUpdateCustomFormInput={() => {}}
                          handleCreateCommonConfig={this.createCommonConfig}
                          handleUpdateCommonConfig={this.updateCommonConfig}
                          handleDeleteCommonConfig={this.deleteCommonConfig}
                        />
                      )}
                      {this.renderPane(
                        "androidConfig",
                        <AndroidConfig
                          isGlobalConfig
                          isSupporting={true}
                          changedPaths={this.state.changedPaths}
                          onRevertPaths={this.handleRevertPaths}
                          toggleSupport={() => {}}
                          handleGetImageLibrary={() => {}}
                          languages={[]}
                          commonSettings={this.props.globalCommonSettings}
                          getSectionDescription={this.getSectionDescription}
                          handleUpdateCustomFormInput={() => {}}
                          handleCreateCommonConfig={this.createCommonConfig}
                          handleUpdateCommonConfig={this.updateCommonConfig}
                          handleDeleteCommonConfig={this.deleteCommonConfig}
                        />
                      )}
                    </KuikaTabContent>
                  </div>
                </div>
              </ConfigRevealContext.Provider>
            </ConfigSearchIndexContext.Provider>
          </KuikaListPage>
        </Form>
        <CloseConfirmationModal
          itemName="Global Configuration"
          closeType="Configuration"
          triggerModal={this.props.showModal}
          // "SAVE & CLOSE" runs the same save; the dialog says it is busy until the save answers.
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeConfig();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </>
    );
  }
}
const mapStateToProps = (state: AppState) => ({
  globalConfigs: state.globalConfig.globalConfigs,
  activeGlobalConfiguration: state.globalConfig.activeGlobalConfiguration,
  globalCommonSettings: state.globalConfig.globalCommonSettings,
  isCreateGlobalConfigActive: state.globalConfig.isCreateGlobalConfigActive,
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setActiveGlobalConfiguration: (config?: IConfig) => dispatch(setActiveGlobalConfiguration(config)),
  setGlobalConfigs: (globalConfigs: IConfig[], globalCommonSettings: ICommonSettings) =>
    dispatch(setGlobalConfigs(globalConfigs, globalCommonSettings)),
  setGlobalConfigsLoadingStatus: (isGlobalConfigsLoading: boolean) =>
    dispatch(setGlobalConfigsLoadingStatus(isGlobalConfigsLoading)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const globalConfigsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<IGlobalConfigsContainerProps>(GlobalConfigsContainer));

export { globalConfigsContainer as GlobalConfigsContainer };
